import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../css/ParticipantList.css";
import "../css/EmailHistory.css";
import "../css/TemplateList.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faPlus, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import EmailDetailsModal from "./EmailDetailsModal";
import { summarizeStatus } from "./communicationStatus";
import DeleteConfirmModal, { useDeleteConfirm } from "./DeleteConfirmModal";
import { GoldLoader } from "../shared/Loaders";

const EmailHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [statusById, setStatusById] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const deleteConfirm = useDeleteConfirm();
  const API_URL = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();

  // Fetch one page of communication history from the server — the backend does the
  // skip/limit, not the browser, so we only ever hold one page's worth of rows in memory.
  const fetchHistory = async (page = currentPage, limit = itemsPerPage) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/ems/communication-history`, { params: { page, limit } });
      setHistory(Array.isArray(res.data.data) ? res.data.data : []);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error("Failed to fetch communication history", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory(currentPage, itemsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage]);

  // Fetches status for every row on the current page in one request (backend fans the
  // Postmark calls out with limited concurrency) — runs whenever a new page of history loads,
  // so badges are ready by the time rows render instead of waiting on a per-row click.
  useEffect(() => {
    if (history.length === 0) return;
    const controller = new AbortController();
    const ids = history.map((item) => item._id);
    axios
      .post(`${API_URL}/ems/communication-history/status/batch`, { ids }, { signal: controller.signal })
      .then((res) => setStatusById(res.data || {}))
      .catch((err) => {
        if (axios.isCancel(err) || err.code === "ERR_CANCELED") return;
        console.error("Failed to fetch email statuses", err);
      });
    // React StrictMode (dev only) double-invokes this effect on mount; without cancelling
    // the first call, both requests hit the backend and double the concurrent Postmark load.
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  const handleDelete = (record) => {
    deleteConfirm.ask({
      id: record._id,
      name: record.subject || record.email || "this record",
    });
  };

  const confirmDelete = async () => {
    const id = deleteConfirm.pending?.id;
    if (!id) return;
    deleteConfirm.setBusy(true);
    try {
      await axios.delete(`${API_URL}/ems/communication-history/${id}`);
      deleteConfirm.clear();
      fetchHistory(currentPage, itemsPerPage);
    } catch (err) {
      console.error("Failed to delete history", err);
      deleteConfirm.setError("Delete failed");
      deleteConfirm.setBusy(false);
    }
  };

  const handleAddcommunication = () => navigate("/ems/templates");
  const handleSendEmail = () => navigate("/ems/send-emails");

  // Search only filters the current page's already-loaded rows — it does not query
  // the backend, so it won't find matches sitting on other pages.
  const currentHistory = history.filter((item) =>
    (item.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.subject || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.body || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const getVisiblePages = () => {
    if (totalPages <= 5) return pageNumbers;
    if (currentPage <= 3) return pageNumbers.slice(0, 5);
    if (currentPage >= totalPages - 2) return pageNumbers.slice(-5);
    return pageNumbers.slice(currentPage - 3, currentPage + 2);
  };

  // Strips HTML tags for the row preview — templates are authored as rich text
  // (<p>, <strong>, <br>), so a raw substring of t.body shows tags as visible text. The
  // full formatted HTML only renders inside EmailDetailsModal, once a row is clicked.
  const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

  const truncateText = (text, length = 50) => {
    const stripped = stripHtml(text);
    if (!stripped) return "-";
    return stripped.length > length ? stripped.slice(0, length) + "..." : stripped;
  };

  if (loading) {
    return <GoldLoader label="Loading..." variant="page" />;
  }

  return (
    <div className="p-4">
      {/* Header with Search on left and Buttons on right - same as Templates list */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center w-25">
          <div className="me-2" style={{ flex: "1" }}>
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search emails..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search emails"
              style={{ border: "1px solid #d1cfcf" }}
            />
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-success"
            onClick={handleAddcommunication}
            style={{ color: "white", borderRadius: "5px", padding: "8px 12px" }}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Template
          </button>
          <button
            className="btn btn-success"
            onClick={handleSendEmail}
            style={{ color: "white", borderRadius: "5px", padding: "8px 12px" }}
          >
            <FontAwesomeIcon icon={faEnvelope} className="me-2" />
            Send Email
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="communication-table-container">
        {/* Header */}
        <div className="communication-header">
          <div className="communication-col col-status">Status</div>
          <div className="communication-col col-name">Email</div>
          <div className="communication-col col-subject">Subject</div>
          <div className="communication-col col-body">Contents</div>
          <div className="communication-col col-date">Created At</div>
            <div className="communication-col col-reference">Reference</div>
          <div className="communication-col col-actions">Actions</div>
        </div>

        {/* Rows */}
        {loading ? (
          <GoldLoader label="Loading..." variant="section" />
        ) : currentHistory.length === 0 ? (
          <p className="text-muted p-2">{searchTerm ? "No emails match your search." : "No communication history available"}</p>
        ) : (
          currentHistory.map((t, i) => (
            <div
              className="communication-row"
              key={t._id || i}
              onClick={() => setSelectedRecord(t)}
              style={{ cursor: "pointer" }}
            >
              <div className="communication-col col-status">
                {statusById[t._id] ? (
                  (() => {
                    const summary = summarizeStatus(statusById[t._id].events, statusById[t._id].status);
                    return <span className={`status-badge ${summary.className}`}>{summary.badge}</span>;
                  })()
                ) : (
                  <span className="status-badge status-badge--neutral">...</span>
                )}
              </div>
              <div className="communication-col col-name">{t.email || "-"}</div>
              <div className="communication-col col-subject">{t.subject || "-"}</div>
              <div className="communication-col col-body">{truncateText(t.body, 40)}</div>
              <div className="communication-col col-date">
                {t.sent_at
                  ? new Date(t.sent_at).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "-"}
              </div>
              <div className="communication-col col-reference">
                {t.referenceId || "-"}
              </div>

              <div className="communication-col col-actions template-actions">
                <button
                  className="btn btn-sm action-btn"
                  onClick={(e) => {
                    e.stopPropagation(); // don't also open the row's details modal
                    handleDelete(t);
                  }}
                  title="Delete"
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              </div>
            </div>
          ))
        )}

        {/* Pagination inside table - same as other tables */}
        {history.length > 0 && (
          <div className="participant-pagination">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: "13px", color: "#666" }}>Show</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "auto", fontSize: "13px" }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="pagination-box">
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>

              {getVisiblePages().map((num) => (
                <button
                  key={num}
                  className={`pagination-btn ${num === currentPage ? "active" : ""}`}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              ))}

              <button
                className="pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedRecord && (
        <EmailDetailsModal
          record={selectedRecord}
          statusSummary={
            statusById[selectedRecord._id]
              ? summarizeStatus(statusById[selectedRecord._id].events, statusById[selectedRecord._id].status)
              : null
          }
          onClose={() => setSelectedRecord(null)}
        />
      )}

      {deleteConfirm.pending && (
        <DeleteConfirmModal
          name={deleteConfirm.pending.name}
          error={deleteConfirm.error}
          busy={deleteConfirm.busy}
          onConfirm={confirmDelete}
          onCancel={deleteConfirm.clear}
        />
      )}
    </div>
  );
};

export default EmailHistory;
